/**
 * React Query hooks for DataForSEO site insights (AB#1035).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { seoApi, type DataForSeoKeyInput, type DataForSeoKeyScope } from '../api/seo';
import { siteHealthKeys } from './useSiteHealth';

export const seoKeys = {
  all: (siteSlug: string) => ['sites', siteSlug, 'seo'] as const,
  insights: (siteSlug: string) => [...seoKeys.all(siteSlug), 'insights'] as const,
};

/** Cached server-side (daily), so this is cheap to load on every visit. */
export function useSeoInsights(siteSlug: string, enabled = true) {
  return useQuery({
    queryKey: seoKeys.insights(siteSlug),
    queryFn: () => seoApi.getInsights(siteSlug),
    enabled: !!siteSlug && enabled,
  });
}

/** Forces a DataForSEO refetch (incl. the page audit) — spends the owner's credit. */
export function useRefreshSeoInsights(siteSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => seoApi.refreshInsights(siteSlug),
    onSuccess: (res) => {
      queryClient.setQueryData(seoKeys.insights(siteSlug), res);
      queryClient.invalidateQueries({ queryKey: siteHealthKeys.detail(siteSlug) });
    },
  });
}

export function useSaveDataForSeoKey(siteSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DataForSeoKeyInput) => seoApi.saveDataForSeoKey(siteSlug, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: seoKeys.all(siteSlug) });
      // Site health carries the DataForSEO rows too (AB#1035).
      queryClient.invalidateQueries({ queryKey: siteHealthKeys.detail(siteSlug) });
    },
  });
}

export function useDeleteDataForSeoKey(siteSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scope?: DataForSeoKeyScope) => seoApi.deleteDataForSeoKey(siteSlug, scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: seoKeys.all(siteSlug) });
      // Site health carries the DataForSEO rows too (AB#1035).
      queryClient.invalidateQueries({ queryKey: siteHealthKeys.detail(siteSlug) });
    },
  });
}
